import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Plus, Search, Pencil, Trash2, X, Sparkles, Loader2, Filter, Printer, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const monthNamesEs = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const monthNamesEn = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatMoney(value, currency = "EUR") {
  if (value == null || isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("es-ES", {
    style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: "always",
  });
}

function formatPct(value) {
  if (value == null || isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
}

// iter 82.7v — El "copy a Gastos" se eliminó: duplicaba el gasto en los
// informes (Salarios + Gastos lo contaban dos veces). Los salarios se
// consolidan en /salarios-lista y aparecen aparte en el P&L.

export default function Salarios() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";

  const [year, setYear] = useState(new Date().getFullYear());
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [conceptFilter, setConceptFilter] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [monthFrom, setMonthFrom] = useState("");
  const [monthTo, setMonthTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Inline editing of individual entries
  const [editing, setEditing] = useState({}); // id -> draft
  const [busyIds, setBusyIds] = useState(new Set());

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState(null);

  const monthNames = lang === "es" ? monthNamesEs : monthNamesEn;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (conceptFilter.trim()) params.concept = conceptFilter.trim();
      if (minAmount !== "" && !isNaN(parseFloat(minAmount))) params.min_amount = parseFloat(minAmount);
      if (maxAmount !== "" && !isNaN(parseFloat(maxAmount))) params.max_amount = parseFloat(maxAmount);
      if (monthFrom !== "") params.month_from = parseInt(monthFrom, 10);
      if (monthTo !== "") params.month_to = parseInt(monthTo, 10);
      const { data } = await api.get("/salary-entries/matrix", { params });
      setMatrix(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [year, conceptFilter, minAmount, maxAmount, monthFrom, monthTo]);

  useEffect(() => { load(); }, [load]);

  const monthlyIncome = matrix?.monthly_income || {};

  // Active months: derived from month_from / month_to or default to 1..12
  const activeMonths = useMemo(() => {
    const from = monthFrom ? parseInt(monthFrom, 10) : 1;
    const to = monthTo ? parseInt(monthTo, 10) : 12;
    const out = [];
    for (let i = Math.max(1, from); i <= Math.min(12, to); i++) out.push(i);
    return out;
  }, [monthFrom, monthTo]);

  // Flatten matrix into one list of entries per month
  const monthRows = useMemo(() => {
    const concepts = matrix?.concepts || [];
    return activeMonths.map((mNum) => {
      const entries = [];
      for (const c of concepts) {
        const cell = c.monthly?.[mNum];
        if (!cell || cell.count === 0) continue;
        for (const e of (cell.entries || [])) {
          entries.push({ ...e, concept: e.concept || c.concept });
        }
      }
      // Sort by date asc within the month
      entries.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      const totalSalaries = entries.reduce((acc, e) => acc + Number(e.amount || 0), 0);
      const income = Number(monthlyIncome[mNum] || 0);
      const marginPct = income > 0 ? (totalSalaries / income) * 100 : null;
      return {
        monthNum: mNum,
        monthLabel: monthNames[mNum - 1],
        entries,
        totalSalaries,
        income,
        marginPct,
      };
    });
  }, [matrix, activeMonths, monthlyIncome, monthNames]);

  const grandSalaries = monthRows.reduce((acc, r) => acc + r.totalSalaries, 0);
  const grandIncome = monthRows.reduce((acc, r) => acc + r.income, 0);
  const grandMarginPct = grandIncome > 0 ? (grandSalaries / grandIncome) * 100 : null;

  const hasActiveFilters = conceptFilter || minAmount !== "" || maxAmount !== "" || monthFrom !== "" || monthTo !== "";

  // iter 82.7q — Export matriz como CSV. Excel abre CSV UTF-8 nativamente, así
  // que entregamos `.csv` (no inventamos XLSX binario en el cliente). Respeta
  // el filtro de meses y concept/importe aplicados.
  const exportMatrixXlsx = () => {
    const sep = ";"; // español locale: separador por defecto en Excel ES
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      if (s.includes(sep) || s.includes("\n") || s.includes('"')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const lines = [];
    lines.push([
      lang === "es" ? "Mes" : "Month",
      lang === "es" ? "Fecha" : "Date",
      lang === "es" ? "Concepto" : "Concept",
      lang === "es" ? "Nº factura" : "Invoice #",
      lang === "es" ? "Notas" : "Notes",
      lang === "es" ? "Importe" : "Amount",
    ].map(esc).join(sep));
    for (const row of monthRows) {
      if (row.entries.length === 0) {
        lines.push([row.monthLabel, "", "", "", "", "0.00"].map(esc).join(sep));
        continue;
      }
      for (const e of row.entries) {
        lines.push([
          row.monthLabel,
          e.date || "",
          e.concept || "",
          e.invoice_number || "",
          e.notes || "",
          Number(e.amount || 0).toFixed(2),
        ].map(esc).join(sep));
      }
    }
    // Resumen por mes
    lines.push([]);
    lines.push([
      lang === "es" ? "Resumen por mes" : "Monthly summary",
    ].map(esc).join(sep));
    lines.push([
      lang === "es" ? "Mes" : "Month",
      lang === "es" ? "Total salarios" : "Total salaries",
      lang === "es" ? "Ingresos" : "Income",
      lang === "es" ? "% sobre ingresos" : "% of income",
    ].map(esc).join(sep));
    for (const row of monthRows) {
      lines.push([
        row.monthLabel,
        row.totalSalaries.toFixed(2),
        row.income.toFixed(2),
        row.marginPct != null ? row.marginPct.toFixed(1) + "%" : "",
      ].map(esc).join(sep));
    }
    lines.push([
      lang === "es" ? "TOTAL" : "TOTAL",
      grandSalaries.toFixed(2),
      grandIncome.toFixed(2),
      grandMarginPct != null ? grandMarginPct.toFixed(1) + "%" : "",
    ].map(esc).join(sep));
    // BOM para que Excel ES/EN detecte UTF-8
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `salarios-matriz-${year}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(lang === "es" ? "Descargado CSV" : "CSV downloaded");
  };

  const clearFilters = () => {
    setConceptFilter("");
    setMinAmount("");
    setMaxAmount("");
    setMonthFrom("");
    setMonthTo("");
  };

  // ============ Inline edit / delete
  const startEdit = (e) => {
    setEditing((prev) => ({
      ...prev,
      [e.id]: {
        concept: e.concept || "",
        date: e.date || "",
        amount: String(e.amount ?? ""),
        invoice_number: e.invoice_number || "",
        notes: e.notes || "",
      },
    }));
  };
  const cancelEdit = (id) => {
    setEditing((prev) => {
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
  };
  const markBusy = (id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const saveEdit = async (e) => {
    const draft = editing[e.id];
    if (!draft) return;
    if (!draft.concept.trim()) { toast.error(lang === "es" ? "Concepto requerido" : "Concept required"); return; }
    const amt = parseFloat(draft.amount);
    if (isNaN(amt)) { toast.error(lang === "es" ? "Importe inválido" : "Invalid amount"); return; }
    markBusy(e.id, true);
    try {
      await api.put(`/salary-entries/${e.id}`, {
        concept: draft.concept.trim(),
        date: draft.date || e.date,
        amount: amt,
        invoice_number: draft.invoice_number || null,
        notes: draft.notes || null,
      });
      toast.success(lang === "es" ? "Guardado" : "Saved");
      cancelEdit(e.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      markBusy(e.id, false);
    }
  };
  const deleteEntry = async (e) => {
    if (!window.confirm(lang === "es" ? `¿Eliminar "${e.concept}" — ${formatMoney(e.amount, baseCurrency)}?` : `Delete "${e.concept}" — ${formatMoney(e.amount, baseCurrency)}?`)) return;
    markBusy(e.id, true);
    try {
      await api.delete(`/salary-entries/${e.id}`);
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      markBusy(e.id, false);
    }
  };

  // ============ Add
  const openAdd = (prefill = null) => {
    setAddDraft(prefill);
    setAddOpen(true);
  };
  const onAddForMonth = (mNum) => {
    const lastDay = new Date(year, mNum, 0).getDate();
    openAdd({
      date: `${year}-${String(mNum).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Cuadro anual" : "Annual board"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Salarios" : "Salaries"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Filas = meses · Una columna Nómina con todos los conceptos del mes. Margen % = coste salarial / facturación."
              : "Rows = months · Single Payroll column listing all concepts per month. Margin % = payroll cost / income."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            data-testid="salaries-toggle-filters"
            className={hasActiveFilters ? "border-[var(--primary)] text-[var(--primary)]" : ""}
          >
            <Filter size={14} className="mr-1.5" />
            {lang === "es" ? "Filtros" : "Filters"}
            {hasActiveFilters && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />}
          </Button>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(parseInt(v, 10) || new Date().getFullYear())}
          >
            <SelectTrigger className="h-9 w-28 font-mono-num" data-testid="salaries-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(() => {
                const cy = new Date().getFullYear();
                // Matrix requires a specific year (no "Total histórico"
                // option — the view is inherently per-year × concept × month).
                return [cy, cy - 1, cy - 2, cy - 3, cy - 4].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
          <PdfUploadButton onDraftReady={(draft) => openAdd(draft)} lang={lang} />
          <Button
            size="sm"
            variant="outline"
            onClick={exportMatrixXlsx}
            data-testid="salaries-export-xlsx"
            title={lang === "es" ? "Descargar matriz como Excel" : "Download matrix as Excel"}
          >
            <FileSpreadsheet size={14} className="mr-1.5" />
            Excel
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.print()}
            data-testid="salaries-print-btn"
            title={lang === "es" ? "Imprimir (guarda como PDF desde el diálogo del navegador)" : "Print (save as PDF from browser dialog)"}
          >
            <Printer size={14} className="mr-1.5" />
            {lang === "es" ? "Imprimir" : "Print"}
          </Button>
          <Button
            size="sm"
            onClick={() => openAdd(null)}
            data-testid="salaries-add-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Añadir manual" : "Add manual"}
          </Button>
        </div>
      </div>

      {/* Filters bar */}
      {showFilters && (
        <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="salaries-filters">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Concepto" : "Concept"}
            </Label>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
              <Input
                value={conceptFilter}
                onChange={(e) => setConceptFilter(e.target.value)}
                placeholder={lang === "es" ? "Filtrar concepto..." : "Filter concept..."}
                className="h-9 pl-8"
                data-testid="filter-concept"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Mes desde" : "Month from"}
            </Label>
            <select
              value={monthFrom}
              onChange={(e) => setMonthFrom(e.target.value)}
              className="h-9 w-full rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm"
              data-testid="filter-month-from"
            >
              <option value="">{lang === "es" ? "(cualquiera)" : "(any)"}</option>
              {monthNames.map((mn, i) => (
                <option key={`from-${mn}`} value={i + 1}>{mn}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Mes hasta" : "Month to"}
            </Label>
            <select
              value={monthTo}
              onChange={(e) => setMonthTo(e.target.value)}
              className="h-9 w-full rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm"
              data-testid="filter-month-to"
            >
              <option value="">{lang === "es" ? "(cualquiera)" : "(any)"}</option>
              {monthNames.map((mn, i) => (
                <option key={`to-${mn}`} value={i + 1}>{mn}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Importe mín" : "Min amount"}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="0"
              className="h-9 font-mono-num"
              data-testid="filter-min-amount"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Importe máx" : "Max amount"}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              placeholder="∞"
              className="h-9 font-mono-num"
              data-testid="filter-max-amount"
            />
          </div>
          {hasActiveFilters && (
            <div className="md:col-span-5 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] underline"
                data-testid="filter-clear"
              >
                {lang === "es" ? "Limpiar filtros" : "Clear filters"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table: rows=months, single "Nómina" column listing all concepts */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="salaries-matrix">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]/40">
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-left py-2 px-3 font-medium min-w-[110px]">
                {lang === "es" ? "Mes" : "Month"}
              </th>
              <th className="text-left py-2 px-3 font-medium">
                {lang === "es" ? "Nómina (conceptos)" : "Payroll (concepts)"}
              </th>
              <th className="text-right py-2 px-3 font-medium min-w-[130px] border-l border-[var(--border-color)] bg-[var(--surface-hover)]/80">
                {lang === "es" ? "Total salarios" : "Total salaries"}
              </th>
              <th className="text-right py-2 px-3 font-medium min-w-[120px]" title={lang === "es" ? "Base imponible (sin IVA)" : "Tax base (excl. VAT)"}>
                {lang === "es" ? "Facturación (BI)" : "Income (Base)"}
              </th>
              <th className="text-right py-2 px-3 font-medium min-w-[90px]">
                {lang === "es" ? "Margen %" : "Margin %"}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-10 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && monthRows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-12 text-[var(--text-muted)]">
                  {lang === "es" ? "Sin meses visibles. Ajusta los filtros." : "No visible months. Adjust filters."}
                </td>
              </tr>
            )}
            {!loading && monthRows.map((r) => (
              <tr
                key={r.monthNum}
                className="border-t border-[var(--border-color)] align-top"
                data-testid={`salary-row-${r.monthNum}`}
              >
                <td className="py-3 px-3 font-semibold text-sm whitespace-nowrap">
                  {r.monthLabel} {year}
                </td>
                <td className="py-2 px-3">
                  <div className="space-y-1.5">
                    {r.entries.length === 0 && (
                      <p className="text-xs text-[var(--text-muted)] italic">
                        {lang === "es" ? "Sin salarios este mes" : "No salaries this month"}
                      </p>
                    )}
                    {r.entries.map((e) => {
                      const draft = editing[e.id];
                      const busy = busyIds.has(e.id);
                      if (draft) {
                        return (
                          <div
                            key={e.id}
                            className="grid grid-cols-12 gap-1.5 items-end p-2 rounded-md bg-[var(--surface-hover)]/50 border border-[var(--primary)]/40"
                            data-testid={`edit-row-${e.id}`}
                          >
                            <div className="col-span-5">
                              <Input
                                value={draft.concept}
                                onChange={(ev) => setEditing((p) => ({ ...p, [e.id]: { ...draft, concept: ev.target.value } }))}
                                placeholder={lang === "es" ? "Concepto" : "Concept"}
                                className="h-8 text-xs"
                                data-testid={`edit-concept-${e.id}`}
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="date"
                                value={draft.date}
                                onChange={(ev) => setEditing((p) => ({ ...p, [e.id]: { ...draft, date: ev.target.value } }))}
                                className="h-8 text-xs font-mono-num"
                                data-testid={`edit-date-${e.id}`}
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={draft.amount}
                                onChange={(ev) => setEditing((p) => ({ ...p, [e.id]: { ...draft, amount: ev.target.value } }))}
                                className="h-8 text-xs font-mono-num text-right"
                                data-testid={`edit-amount-${e.id}`}
                              />
                            </div>
                            <div className="col-span-2 flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => saveEdit(e)}
                                disabled={busy}
                                data-testid={`save-${e.id}`}
                                className="h-8 px-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                              >
                                ✓
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelEdit(e.id)}
                                className="h-8 px-2"
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={e.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-[var(--border-color)] hover:bg-[var(--surface-hover)]/30 group"
                          data-testid={`entry-row-${e.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" title={e.concept}>
                              {e.concept}
                              {e.invoice_number && (
                                <span className="ml-2 text-[10px] text-[var(--text-muted)] font-mono-num">#{e.invoice_number}</span>
                              )}
                            </p>
                            <p className="text-[10px] text-[var(--text-muted)] font-mono-num">
                              {formatDateShort(e.date)}
                              {e.notes && <span> · {e.notes}</span>}
                            </p>
                          </div>
                          <span className="font-mono-num font-semibold text-sm" data-testid={`entry-amount-${e.id}`}>
                            {formatMoney(e.amount, baseCurrency)}
                          </span>
                          <div className="flex gap-0.5 opacity-50 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEdit(e)}
                              disabled={busy}
                              data-testid={`edit-btn-${e.id}`}
                              className="h-7 w-7 p-0"
                            >
                              <Pencil size={12} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteEntry(e)}
                              disabled={busy}
                              data-testid={`delete-btn-${e.id}`}
                              className="h-7 w-7 p-0 text-[var(--status-negative)] hover:text-[var(--status-negative)]"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAddForMonth(r.monthNum)}
                      data-testid={`add-for-month-${r.monthNum}`}
                      className="h-7 text-xs w-full justify-center border-dashed text-[var(--text-muted)] hover:text-[var(--primary)] hover:border-[var(--primary)]"
                    >
                      <Plus size={12} className="mr-1" />
                      {lang === "es" ? "Añadir salario" : "Add salary"}
                    </Button>
                  </div>
                </td>
                <td
                  className="py-3 px-3 text-right font-mono-num font-semibold border-l border-[var(--border-color)] bg-[var(--surface-hover)]/20"
                  data-testid={`row-total-${r.monthNum}`}
                >
                  {formatMoney(r.totalSalaries, baseCurrency)}
                </td>
                <td
                  className="py-3 px-3 text-right font-mono-num text-[var(--text-secondary)]"
                  data-testid={`row-income-${r.monthNum}`}
                >
                  {r.income > 0 ? formatMoney(r.income, baseCurrency) : "—"}
                </td>
                <td
                  className={`py-3 px-3 text-right font-mono-num font-semibold ${
                    r.marginPct == null
                      ? "text-[var(--text-muted)]"
                      : r.marginPct > 40
                        ? "text-[var(--status-negative)]"
                        : r.marginPct > 25
                          ? "text-amber-500"
                          : "text-[var(--status-positive)]"
                  }`}
                  data-testid={`row-margin-${r.monthNum}`}
                  title={r.marginPct != null ? (lang === "es" ? "Coste salarial sobre facturación" : "Payroll cost over income") : ""}
                >
                  {formatPct(r.marginPct)}
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && monthRows.length > 0 && (
            <tfoot data-testid="salaries-footer">
              <tr className="border-t-2 border-[var(--border-color)] bg-[var(--surface-hover)]/40 font-semibold">
                <td colSpan={2} className="py-3 px-3 uppercase text-xs tracking-wider">
                  {lang === "es" ? "Total año" : "Year total"}
                </td>
                <td className="py-3 px-3 text-right font-mono-num text-base border-l border-[var(--border-color)]" data-testid="salaries-grand-total">
                  {formatMoney(grandSalaries, baseCurrency)}
                </td>
                <td className="py-3 px-3 text-right font-mono-num" data-testid="salaries-grand-income">
                  {grandIncome > 0 ? formatMoney(grandIncome, baseCurrency) : "—"}
                </td>
                <td
                  className={`py-3 px-3 text-right font-mono-num ${
                    grandMarginPct == null
                      ? "text-[var(--text-muted)]"
                      : grandMarginPct > 40
                        ? "text-[var(--status-negative)]"
                        : grandMarginPct > 25
                          ? "text-amber-500"
                          : "text-[var(--status-positive)]"
                  }`}
                  data-testid="salaries-grand-margin"
                >
                  {formatPct(grandMarginPct)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <EntryDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setAddDraft(null); }}
        draft={addDraft}
        defaultYear={year}
        onSaved={() => { setAddOpen(false); setAddDraft(null); load(); }}
        lang={lang}
      />
    </div>
  );
}

// ============================================================ PDF upload (single)
function PdfUploadButton({ onDraftReady, lang }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/salary-entries/process-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.error) {
        toast.warning(lang === "es" ? `OCR parcial: ${data.error}. Edita y guarda.` : `OCR partial: ${data.error}. Edit and save.`);
      } else {
        toast.success(lang === "es" ? "PDF procesado — revisa y guarda" : "PDF processed — review and save");
      }
      onDraftReady({ ...(data.draft || {}), file_id: data.file_id });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="salaries-pdf-upload"
      >
        {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5 text-[var(--primary)]" />}
        {lang === "es" ? "Subir PDF (IA)" : "Upload PDF (AI)"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </>
  );
}

// ============================================================ Add single entry
function EntryDialog({ open, onClose, draft, defaultYear, onSaved, lang }) {
  const [concept, setConcept] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setConcept(draft?.concept || "");
      setDate(draft?.date || `${defaultYear}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`);
      setAmount(draft?.amount != null ? String(draft.amount) : "");
      setInvoiceNumber(draft?.invoice_number || "");
      setNotes(draft?.notes || "");
    }
  }, [open, draft, defaultYear]);

  const submit = async () => {
    if (!concept.trim()) {
      toast.error(lang === "es" ? "Concepto requerido" : "Concept required");
      return;
    }
    if (!date) {
      toast.error(lang === "es" ? "Fecha requerida" : "Date required");
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt)) {
      toast.error(lang === "es" ? "Importe inválido" : "Invalid amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/salary-entries", {
        concept: concept.trim(),
        date,
        amount: amt,
        invoice_number: invoiceNumber || null,
        notes: notes || null,
      });
      // iter 82.7v — ya no ofrecemos "añadir a Gastos": duplicaba el gasto en
      // los informes (se sumaba en Salarios Y en Gastos). El P&L consolida
      // salarios como línea aparte.
      toast.success(lang === "es" ? "Salario añadido" : "Salary added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="entry-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Añadir salario" : "Add salary"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              {lang === "es" ? "Concepto / Empleado" : "Concept / Employee"}
            </Label>
            <Input
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder={lang === "es" ? "Ej: Juan Pérez · Cocinero" : "e.g. Juan · Cook"}
              data-testid="entry-concept"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Fecha" : "Date"}
              </Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="entry-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Importe" : "Amount"}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="entry-amount"
                className="font-mono-num"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Nº recibo" : "Receipt #"}
              </Label>
              <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="entry-number" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Notas" : "Notes"}
              </Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="entry-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving} data-testid="entry-submit" className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {saving ? "..." : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
